// src/pages/EditBookRelease.jsx
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useParams, useNavigate } from 'react-router-dom';

function EditBookRelease() {
  const { releaseId } = useParams();
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [languageCode, setLanguageCode] = useState('');
  const [bookId, setBookId] = useState('');
  const [bookName, setBookName] = useState('');
  const [description, setDescription] = useState('');

  const [releaseDate, setReleaseDate] = useState('');

  // File inputs
  const [coverFile, setCoverFile] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);

  // Existing links/previews
  const [existingCoverUrl, setExistingCoverUrl] = useState('');
  const [existingPdfUrl, setExistingPdfUrl] = useState('');

  const [coverPdfPreviewUrl, setCoverPdfPreviewUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');

  const handleCoverFileChange = (e) =>
    setCoverFile(e.target.files?.[0] || null);
  const handlePdfFileChange = (e) => setPdfFile(e.target.files?.[0] || null);

  // Fetch release details
  useEffect(() => {
    if (!releaseId) return;
    (async () => {
      try {
        const { data } = await axios.get(`/api/book-release/${releaseId}`);
        setName(data?.name || '');
        setLanguageCode(data?.language_code || '');
        setExistingCoverUrl(data?.cover_page_url || '');
        setExistingPdfUrl(data?.release_pdf_url || '');
        setBookId(data?.book_id || '');
        setDescription(data?.description || '');
        const d = data?.release_date ? new Date(data.release_date) : null;
        if (d && !Number.isNaN(d.getTime())) {
          const yyyy = d.getFullYear();
          const mm = String(d.getMonth() + 1).padStart(2, '0');
          const dd = String(d.getDate()).padStart(2, '0');
          setReleaseDate(`${yyyy}-${mm}-${dd}`);
        }
        // Fetch book name if we have bookId
        if (data?.book_id) {
          const bookResp = await axios.get(`/api/book/${data.book_id}`);
          setBookName(bookResp?.data?.name || '');
        }
      } catch (e) {
        console.error('Failed to load release', e);
        setMsg('Failed to load release');
      }
    })();
  }, [releaseId]);

  // When a cover file is picked, create an object URL so user can open it
  useEffect(() => {
    if (coverFile) {
      const url = URL.createObjectURL(coverFile);
      setCoverPdfPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    setCoverPdfPreviewUrl('');
  }, [coverFile]);

  const validate = () => {
    if (!releaseId) return 'Missing release id.';
    if (!languageCode) return 'Please choose a language profile.';
    if (!releaseDate) return 'Please select a release date.';
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMsg('');
    const error = validate();
    if (error) {
      setMsg(error);
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      if (name && name.trim()) formData.append('name', name.trim());
      if (languageCode) formData.append('language_code', languageCode);
      if (releaseDate) formData.append('release_date', releaseDate);
      if (coverFile) formData.append('cover', coverFile);
      if (pdfFile) formData.append('pdf', pdfFile);
      if (description !== undefined)
        formData.append('description', description);

      await axios.patch(`/api/book-releases/${releaseId}`, formData);
      setMsg('Release updated.');
      setTimeout(() => {
        if (bookId) {
          navigate(`/book-releases/${bookId}?plang=${languageCode || ''}`);
        }
      }, 500);
    } catch (err) {
      console.error(err);
      setMsg('Error updating the release. Try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-6 bg-white shadow-lg rounded-lg mt-10">
      <h2 className="text-2xl font-bold text-center mb-4">Edit Release</h2>

      {msg && <p className="text-center text-red-500 mb-4">{msg}</p>}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-gray-700 font-medium">
            Release name
          </label>
          <input
            type="text"
            placeholder={bookName || 'Release name'}
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full p-2 border border-gray-300 rounded mt-1"
          />
        </div>

        <div>
          <label className="block text-gray-700 font-medium">Description</label>
          <textarea
            rows={4}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full p-2 border border-gray-300 rounded mt-1"
            placeholder="Optional description of this release"
          />
        </div>

        <div>
          <label className="block text-gray-700 font-medium">
            Language profile
          </label>
          <select
            value={languageCode}
            onChange={(e) => setLanguageCode(e.target.value)}
            className="w-full p-2 border border-gray-300 rounded mt-1"
          >
            <option value="">Choose…</option>
            <option value="es_de">ES-DE</option>
            <option value="de_en">DE-EN</option>
            <option value="es_en">ES-EN</option>
            <option value="nl_en">NL-EN</option>
            <option value="es_ru">ES-RU</option>
            <option value="de_ru">DE-RU</option>
            <option value="es_it">ES-IT</option>
            <option value="de_it">DE-IT</option>
            <option value="es_fr">ES-FR</option>
            <option value="de_fr">DE-FR</option>
            <option value="de_tr">DE-TR</option>
            <option value="es_tr">ES-TR</option>
            <option value="de_pl">DE-PL</option>
            <option value="es_pl">ES-PL</option>
            <option value="de">DE</option>
            <option value="fr">FR</option>
            <option value="it">IT</option>
            <option value="es">ES</option>
            <option value="ru">RU</option>
            <option value="nl">NL</option>
            <option value="tr">TR</option>
            <option value="pl">PL</option>
          </select>
        </div>

        <div>
          <label className="block text-gray-700 font-medium">
            Release date
          </label>
          <input
            type="date"
            value={releaseDate}
            onChange={(e) => setReleaseDate(e.target.value)}
            className="w-full p-2 border border-gray-300 rounded mt-1"
          />
        </div>

        <div>
          <label className="block text-gray-700 font-medium mb-1">
            Cover (PDF)
          </label>
          {existingCoverUrl ? (
            <div className="text-xs text-gray-600 mb-2">
              Current:{' '}
              <a
                className="text-blue-600 hover:underline"
                href={existingCoverUrl}
                target="_blank"
                rel="noreferrer"
              >
                {existingCoverUrl}
              </a>
            </div>
          ) : (
            <p className="text-xs text-gray-500 mb-2">No cover set</p>
          )}
          <input
            type="file"
            accept="application/pdf"
            onChange={handleCoverFileChange}
            className="w-full p-2 border border-gray-300 rounded mt-2"
          />
          {coverPdfPreviewUrl && (
            <div className="mt-2">
              <a
                href={coverPdfPreviewUrl}
                target="_blank"
                rel="noreferrer"
                className="text-blue-600 hover:underline text-xs"
              >
                Open selected cover (PDF)
              </a>
            </div>
          )}
        </div>

        <div>
          <label className="block text-gray-700 font-medium mb-1">
            Release PDF
          </label>
          {existingPdfUrl ? (
            <div className="text-xs text-gray-600 mb-2">
              Current:{' '}
              <a
                className="text-blue-600 hover:underline"
                href={existingPdfUrl}
                target="_blank"
                rel="noreferrer"
              >
                {existingPdfUrl}
              </a>
            </div>
          ) : (
            <p className="text-xs text-gray-500 mb-2">No release PDF set</p>
          )}
          <input
            type="file"
            accept="application/pdf"
            onChange={handlePdfFileChange}
            className="w-full p-2 border border-gray-300 rounded mt-2"
          />
        </div>

        <div className="mt-6 flex justify-center items-center gap-4">
          <button
            type="submit"
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
            disabled={loading}
          >
            {loading ? 'Saving…' : 'Save Changes'}
          </button>

          {bookId && (
            <Link
              to={`/book-releases/${bookId}?plang=${languageCode || ''}`}
              className="bg-gray-100 text-gray-800 px-4 py-2 rounded hover:bg-gray-200"
            >
              Cancel
            </Link>
          )}
        </div>
      </form>
    </div>
  );
}

export default EditBookRelease;
